/**
 * Validated Input Component
 * Provides real-time input validation and sanitization
 */

import React, { useState, useEffect, useCallback } from 'react';
import { validateInput, sanitizeInput } from '../utils/inputValidator';
import './ValidatedInput.css';

const ValidatedInput = ({
  type = 'text',
  ruleType = 'TEXT',
  value,
  onChange,
  onValidationChange,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  options = {},
  showValidation = true,
  ...props
}) => {
  const [validationResult, setValidationResult] = useState({
    isValid: true,
    errors: [],
    warnings: []
  });
  const [isValidating, setIsValidating] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  // Debounced validation
  const validateInputDebounced = useCallback(
    debounce(async (inputValue) => {
      if (!inputValue && !required) {
        setValidationResult({ isValid: true, errors: [], warnings: [] });
        return;
      }

      setIsValidating(true);
      
      try {
        const result = await validateInput(inputValue, ruleType, options);
        setValidationResult(result);
        
        if (onValidationChange) {
          onValidationChange(result);
        }
      } catch (error) {
        console.error('Validation error:', error);
        setValidationResult({
          isValid: false,
          errors: ['Validation error occurred'],
          warnings: []
        });
      } finally {
        setIsValidating(false);
      }
    }, 300),
    [ruleType, options, required, onValidationChange]
  );

  // Validate on value change
  useEffect(() => {
    if (value !== undefined && value !== null) {
      validateInputDebounced(value);
    }
  }, [value, validateInputDebounced]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Sanitize input in real-time
    const sanitizedValue = sanitizeInput(inputValue, options);
    
    // Call onChange with sanitized value
    if (onChange) {
      onChange({
        ...e,
        target: {
          ...e.target,
          value: sanitizedValue
        }
      });
    }
  };

  const handleBlur = () => {
    setHasBlurred(true);
  };

  const getInputClassName = () => {
    let baseClass = 'validated-input';
    
    if (className) {
      baseClass += ` ${className}`;
    }
    
    if (hasBlurred && !validationResult.isValid) {
      baseClass += ' validated-input--error';
    } else if (hasBlurred && validationResult.isValid && value) {
      baseClass += ' validated-input--valid';
    }
    
    if (isValidating) {
      baseClass += ' validated-input--validating';
    }
    
    return baseClass;
  };

  const shouldShowValidation = showValidation && hasBlurred && (validationResult.errors.length > 0 || validationResult.warnings.length > 0);

  return (
    <div className="validated-input-container">
      <input
        type={type}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={getInputClassName()}
        {...props}
      />
      
      {isValidating && (
        <div className="validated-input-spinner">
          <div className="spinner"></div>
        </div>
      )}
      
      {shouldShowValidation && (
        <div className="validated-input-feedback">
          {validationResult.errors.map((error, index) => (
            <div key={index} className="validated-input-error">
              ❌ {error}
            </div>
          ))}
          {validationResult.warnings.map((warning, index) => (
            <div key={index} className="validated-input-warning">
              ⚠️ {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default ValidatedInput;
